import React, { useState, useMemo, useCallback } from 'react';

import { Button, NavLinkButton } from '@shared/components/bootstrap';
import { Modal } from '@shared/components/bootstrap';
import { ILightboxCarouselInput, LightboxWithCarousel } from '@shared/components/helpers/lightbox';

import { Panel } from '@admin/components/helpers/panel';
import { Table as HistoriesTable } from '@admin/components/histories/table';
import { ItemCategorizationModal } from '@admin/components/items/item_categorization_modal';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import { useAttachedItemsQuery, History__Subject, AttachedItemsQuery } from '@admin/schema';
import { IImage } from '@admin/types/image';
import { IItem } from '@admin/types/item';
import { normalizeItemToGraphQL } from '@admin/utils/item_normalization';

import { ItemsGrid } from './items_grid';

type ItemGroup = NonNullable<AttachedItemsQuery['itemGroup']>;
type GraphQLItem = ItemGroup['items'][number];
type GraphQLImage = GraphQLItem['images'][number];

// Helper to convert GraphQL image to IImage format
const convertGraphQLImage = (img: GraphQLImage) => ({
  id: Number(img.id),
  state: img.state,
  source: img.source,
  size: img.size ?? '',
  name: img.name ?? '',
  createdAt: img.createdAt ?? '',
});

// Helper to convert GraphQL item to IItem format
const convertGraphQLItemToIItem = (item: GraphQLItem, includePackaging = false): IItem => {
  const accountIDNum = Number(item.accountID);
  return normalizeItemToGraphQL({
    id: Number(item.id),
    hero: item.hero,
    barcode: {
      id: Number(item.barcode.id),
      value: item.barcode.value,
    },
    category: item.category ?? undefined,
    state: item.state,
    images: item.images.map(convertGraphQLImage),
    accountID: accountIDNum,
    account_id: accountIDNum,
    ...(includePackaging && item.packaging
      ? {
          packaging: {
            type: item.packaging.toLowerCase() as 'boxed' | 'crated' | 'wrapped',
            materialCategoryId: item.materialCategory ? Number(item.materialCategory.id) : undefined,
            partCategoryId: item.partCategory ? Number(item.partCategory.id) : undefined,
          },
        }
      : {}),
  });
};

export const AttachedItems: React.FC<{
  itemGroupID?: string | null;
  accountID: string;
  timezone?: string;
}> = ({ itemGroupID, accountID, timezone }) => {
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [imageSelection, setImageSelection] = useState<number | undefined>();

  const { data, loading, error, refetch } = useAttachedItemsQuery({
    client,
    variables: {
      itemGroupID: itemGroupID ? String(itemGroupID) : '',
    },
    skip: !itemGroupID,
  });

  const itemGroup = data?.itemGroup;

  const handleManage = useCallback(() => {
    if (!itemGroup) return;
    setShowManageModal(true);
  }, [itemGroup]);

  const handleModalSave = useCallback(() => {
    setShowManageModal(false);
    refetch();
  }, [refetch]);

  const handleCloseHistoryModal = useCallback(() => {
    setShowHistoryModal(false);
  }, []);

  const handleCloseManageModal = useCallback(() => {
    setShowManageModal(false);
  }, []);

  const handleOpenHistoryModal = useCallback(() => {
    setShowHistoryModal(true);
  }, []);

  const handleImageClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setImageSelection(0);
  }, []);

  // Memoize item conversions to avoid recalculating on every render
  const itemsForModal: IItem[] = useMemo(() => {
    if (!itemGroup) return [];
    return itemGroup.items.map((item) => convertGraphQLItemToIItem(item, true));
  }, [itemGroup]);

  const heroImageForModal: IImage | null = useMemo(() => {
    if (!itemGroup?.heroImage) return null;
    return {
      id: Number(itemGroup.heroImage.id),
      state: itemGroup.heroImage.state,
      source: itemGroup.heroImage.source,
      size: itemGroup.heroImage.size ?? '',
      name: itemGroup.heroImage.name ?? '',
      createdAt: itemGroup.heroImage.createdAt ?? '',
    };
  }, [itemGroup?.heroImage]);

  const imageInputs: ILightboxCarouselInput[] = useMemo(
    () => (itemGroup?.heroImage ? [{ src: itemGroup.heroImage.source }] : []),
    [itemGroup?.heroImage],
  );

  // Early returns for loading and empty states
  if (!itemGroupID) {
    return (
      <Panel>
        <Panel.Header>
          <Panel.Title>Attached Items</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p className="text-center">We do not have a record of any disassembly for this item.</p>
        </Panel.Body>
        <Panel.Footer>
          <div className="clearfix">
            <div className="pull-right">
              <NavLinkButton to={`/accounts/${accountID}/item_groups/new`} kind="default">
                Attach Items
              </NavLinkButton>
            </div>
          </div>
        </Panel.Footer>
      </Panel>
    );
  }

  if (loading) {
    return (
      <Panel>
        <Panel.Header>
          <Panel.Title>Attached Items</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <Spinner />
        </Panel.Body>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel>
        <Panel.Header>
          <Panel.Title>Attached Items</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p className="text-center">
            {error.message || 'An error occurred while loading attached items. Please try again.'}
          </p>
        </Panel.Body>
      </Panel>
    );
  }

  if (!itemGroup) {
    return (
      <Panel>
        <Panel.Header>
          <Panel.Title>Attached Items</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p className="text-center">We do not have a record of any disassembly for this item.</p>
        </Panel.Body>
      </Panel>
    );
  }

  const heroImage = itemGroup.heroImage;

  return (
    <>
      <Panel>
        <Panel.Header>
          <Panel.Title>Attached Items</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <div className="media">
            <div className="media-left media-middle">
              {heroImage && (
                <a href="#" onClick={handleImageClick}>
                  <img
                    src={heroImage.size ?? heroImage.source}
                    width={160}
                    height={160}
                    className="media-object"
                    alt="Item group hero"
                  />
                </a>
              )}
            </div>
            <div className="media-body media-middle">
              <p className="media-heading">
                {itemGroup.customCategoryName
                  ? `Custom (${itemGroup.customCategoryName})`
                  : (itemGroup.category?.name ?? 'Item group')}
                {itemGroup.sizingSelection && ` (${itemGroup.sizingSelection})`}
              </p>
              {itemGroup.length && itemGroup.width && itemGroup.height && (
                <p className="media-content">
                  {itemGroup.length}" × {itemGroup.width}" × {itemGroup.height}"
                </p>
              )}
            </div>
          </div>
        </Panel.Body>
        <Panel.Footer>
          <div className="clearfix">
            <div className="pull-right">
              <Button onClick={handleManage} kind="default">
                Manage
              </Button>
              <Button onClick={handleOpenHistoryModal} kind="default">
                History
              </Button>
            </div>
          </div>
        </Panel.Footer>
      </Panel>

      {itemGroup.items.length > 0 && (
        <ItemsGrid items={itemGroup.items.map((item) => convertGraphQLItemToIItem(item))} />
      )}

      {imageSelection != null && imageInputs.length > 0 && (
        <LightboxWithCarousel
          imageInputs={imageInputs}
          defaultActiveIndex={imageSelection}
          onClose={() => setImageSelection(undefined)}
        />
      )}

      {showHistoryModal && itemGroupID && (
        <Modal onClose={handleCloseHistoryModal}>
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>History</Modal.Title>
              <Modal.Close close={handleCloseHistoryModal} />
            </Modal.Header>
            <Modal.Body>
              <HistoriesTable id={itemGroupID} type={History__Subject.ItemGroup} timezone={timezone} />
            </Modal.Body>
          </Modal.Content>
        </Modal>
      )}

      {showManageModal && itemGroup && (
        <ItemCategorizationModal
          items={itemsForModal}
          accountID={accountID}
          mode="item_group"
          itemGroupID={itemGroup.id}
          initialCategory={
            itemGroup.category
              ? {
                  id: itemGroup.category.id,
                  name: itemGroup.category.name,
                }
              : null
          }
          initialSizingSelection={itemGroup.sizingSelection || null}
          initialCustomCategoryName={itemGroup.customCategoryName || null}
          initialLength={itemGroup.length || null}
          initialWidth={itemGroup.width || null}
          initialHeight={itemGroup.height || null}
          initialHeroImage={heroImageForModal}
          initialDimensionsOverwritten={itemGroup.dimensionsOverwritten ?? false}
          onClose={handleCloseManageModal}
          onSave={handleModalSave}
        />
      )}
    </>
  );
};
